import { useCallback, type Dispatch } from 'react';
import {
  detectMaxRegression,
  parseLocaleNumber,
  restInviteSeconds,
  type CurrentMax,
  type Exercise,
  type PRsByExercise,
} from '@heraion/shared';

import { detectSetPr, priorSessionSetsForPr } from './prDetect';
import { placeCompletedSetAtTail } from './timelinePlacement';
import {
  resolveRestSeconds,
  resolveRestTransition,
  type RestTransition,
} from './restTransition';
import type { UseRestTimer } from './useRestTimer';
import { canonicalRirOf } from './state/canonicalRir';
import { isPrescribedBoutSet } from './deriveEntryKind';
import type { DraftEntry, LoggerAction } from './state/loggerDraftTypes';
import type { LoggerMode } from './loggerMode';

// Per-set pill cycle orchestration extracted from LoggerScreen (Track
// R). The pending→done leg pre-computes the PR cue (detectSetPr, pure +
// unit-tested), dispatches
// the cycle_set action and fires the rest timer; the other legs just
// dispatch (the reducer clears prFlash). Pre-compute happens here because
// the reducer is a pure state machine that can't reach the screen-level
// records fetch. Falls back to no PR cue when prHistory hasn't hydrated
// yet rather than blocking the press (would feel broken on cold gym 5G).
export function useSetCyclePill(args: {
  entries: readonly DraftEntry[];
  dispatch: Dispatch<LoggerAction>;
  prHistory: PRsByExercise | null;
  /** Stored MAX + setAt per exercise (from MaxesContext.maxMeta). Drives
   *  the bidirectional MAX-regression ⬇️ marker (next/measurement-contract.md «Il MAX dinamico: il come»).
   *  null until the maxes map has loaded — the regression check then
   *  simply doesn't fire (no false ⬇️ against an empty map), same defence
   *  as prHistory. */
  currentMaxes: Map<string, CurrentMax> | null;
  catalog: Exercise[] | null;
  defaultRestSec: number | null | undefined;
  restTimer: UseRestTimer;
  /** Session start, used to anchor in-window placement when editing a
   *  past session. */
  startedAt: string;
  /** What the logger core is doing (PE-22 slice A). Asked TWO separate
   *  questions here: the clock (`'log-past'` places a completion
   *  in-window-at-tail instead of stamping the live one, next/timeline.md)
   *  and the live cues (rest countdown + bodyweight marker, `'log'` only). */
  mode: LoggerMode;
  /** Fired on the pending→done leg of a BODYWEIGHT set. The screen uses
   *  it to stamp Profile.bodyweightNeededAt (once) when bodyweightKg is
   *  unset: the server then derives the bell row (#296). Nothing is shown
   *  in session. The guard lives at the screen level; here we only signal. */
  onBodyweightSetCompleted?: () => void;
}) {
  const {
    entries,
    dispatch,
    prHistory,
    currentMaxes,
    catalog,
    defaultRestSec,
    restTimer,
    startedAt,
    mode,
    onBodyweightSetCompleted,
  } = args;
  return useCallback(
    // opts.rir (AP-0b): the effort meter's tap both WRITES the rir and
    // completes the set in one gesture. The caller queues the update_set
    // write before this dispatch, so the STATE lands right — but this
    // callback's `entries` closure is pre-dispatch, and the MAX-regression
    // check qualifies a maximal-effort set on rir === 0. Without the
    // override it would always read the PREVIOUS rir and miss the very tap
    // that says "nothing left". Returns { prFlash } so the meter can pulse
    // the tapped segment gold (the binding advances immediately after, so
    // the caller can't read it back off the set), and { restArmed } so the
    // caller knows whether the focus transition has a rest to sit through
    // (MX-5 slice 3b) or has to be applied at once. `restInviteSec` is the
    // longer rest to OFFER after a set near failure (absent = none); `opts.fail`
    // says the ✗ closed it, since the fail leg is dispatched after this call.
    (
      entryIdx: number,
      setIdx: number,
      opts?: { rir?: number; fail?: boolean; transition?: RestTransition },
    ): { prFlash: boolean; restArmed: boolean; restInviteSec?: number } => {
      const entry = entries[entryIdx];
      const setRow = entry?.sets[setIdx];
      if (!entry || !setRow) {
        dispatch({ type: 'cycle_set', entryIdx, setIdx });
        return { prFlash: false, restArmed: false };
      }
      // Only the pending→done transition runs the PR check; the other
      // legs clear prFlash inside the reducer regardless of what we
      // pass here.
      if (setRow.completedAt !== null) {
        dispatch({ type: 'cycle_set', entryIdx, setIdx });
        return { prFlash: false, restArmed: false };
      }
      const { built, isPr: prFlash } = detectSetPr({
        set: setRow,
        exerciseId: entry.exerciseId,
        isBodyweight: entry.isBodyweight,
        prHistory,
        // Candidate is still pending (completedAt null) → every set of
        // this exercise already completed in the session counts, so an
        // identical 2nd set doesn't re-flash a trophy the 1st owns.
        priorSessionSets: priorSessionSetsForPr(
          entries,
          entry.exerciseId,
          entry.isBodyweight,
          null,
        ),
      });
      // Bidirectional MAX (next/measurement-contract.md «Il MAX dinamico: il come»): the DOWNWARD twin of the
      // PR check. Reuse the same `built` WorkoutSet — a qualifying
      // maximal-effort set (RIR 0 / fail) that computes ≥δ below a stale
      // stored MAX earns the red ⬇️. Single-set window: the per-set marker
      // is exactly detectMaxRegression over [built]. Doesn't fire until
      // maxes load (currentMaxes null), mirroring the prHistory guard.
      const currentMax = currentMaxes?.get(entry.exerciseId) ?? null;
      // buildWorkoutSetFromDraft carries no rir/fail (save-time payload adds
      // them later), so the ceiling qualification (rir === 0) must come from
      // the meter's opts.rir — attached here, on the set the check reads.
      const builtForCeiling =
        built !== null && typeof opts?.rir === 'number'
          ? { ...built, rir: opts.rir }
          : built;
      const regressionFlash =
        builtForCeiling !== null &&
        currentMax !== null &&
        detectMaxRegression({
          sets: [builtForCeiling],
          currentMax,
          nowMs: Date.now(),
        }) !== null;
      // Past-session edit: place the set in-window-at-tail instead of
      // stamping the live clock (next/timeline.md). Live logging omits
      // completedAt so the reducer uses `now`.
      // ⚠️ This is the CLOCK question, so it names 'log-past' exactly — it
      // must NOT widen to `mode !== 'log'`. A PRESCRIBED set was never
      // performed and has no completion at all; stamping one here is how a
      // prescription became a measurement. Slice D wires that path.
      const completedAt = mode === 'log-past'
        ? placeCompletedSetAtTail(entries, startedAt)
        : undefined;
      // ⛔ NO `mode === 'prescribe'` guard here, deliberately (PE-22 fetta C).
      // A prescribed set must never reach this dispatch — it would be completed
      // with the reducer's `now`, which is the exact failure the comment above
      // names. The decision is made ONE level up, in useEffortCommit, which is
      // where the mode question belongs and where it is pinned by tests. A
      // silent return here would make a wiring mistake above look like correct
      // behaviour instead of surfacing it ([[feedback_a_fail_closed_guard_hides_the_bug_above_it]]).
      dispatch({
        type: 'cycle_set',
        entryIdx,
        setIdx,
        prFlash,
        regressionFlash,
        ...(completedAt ? { completedAt } : {}),
      });
      // Whether a countdown actually started. Stays false on a past-session
      // edit and on a zero-length rest, and the caller reads it as "apply the
      // focus transition now, there is nothing to sit through".
      let restArmed = false;
      let restInviteSec = 0;
      // Fire the rest timer on the pending→done leg — LIVE logging ONLY.
      // Editing a past saved session must NOT arm a rest countdown: the user
      // is correcting history, not training now, so a rest/execution cue is
      // out of place (Antenore 2026-07-10 — "in edit non dovrebbe partire il
      // timer del rest, cose che sono proprie di un logger e non di un
      // editor"). Same guard that already skips the bodyweight marker below.
      if (mode === 'log') {
        // The rest length rides on the SAME "what comes next" answer that
        // moves the focus (MX-5 slice 3b): resolveRestTransition is asked once
        // by the caller and handed down, so the countdown and the transition
        // can't disagree. Without a caller-supplied one we resolve it here,
        // through the same function, with no group members.
        // ⚠️ DEFENSIVE AND CURRENTLY DEAD: the only live caller (the timeline
        // logger's commitSet) always supplies a transition, and the only path
        // that omits one is a past-session edit, which never reaches this block
        // at all. It is here so a future caller gets a rest rather than none —
        // but it would get the STANDALONE length even inside a superset. If you
        // add a caller, thread the transition through instead of trusting this
        // branch: it has no test because it has no user.
        const transition =
          opts?.transition ??
          resolveRestTransition({
            entries,
            entryIdx,
            loggedSetIdx: setIdx,
            groupMembers: [],
          });
        const ex = catalog?.find((x) => x.slug === entry.exerciseId);
        const seconds = resolveRestSeconds({
          transition,
          // The rest the plan wrote for THIS set (HI-1): "30s hard, 90s
          // easy" only holds if the 90 outranks the catalog default.
          prescribedRestSec: setRow.restPrescribedSec,
          exerciseDefaultRestSec: ex?.defaultRestSec,
          profileDefaultRestSec: defaultRestSec,
        });
        // Stop any rest still running/paused before re-arming. start()
        // early-returns when seconds <= 0 (an exercise with
        // defaultRestSec = 0 means "no rest"), so without this the old
        // timer would keep ticking + its notification stay scheduled when
        // you complete a set on a zero-rest lift. For a normal restart
        // this is behaviour-neutral (reset cancels, start re-schedules).
        if (restTimer.state.endsAt !== null || restTimer.state.isPaused) {
          restTimer.reset();
        }
        // TM-12: a rest is armed at the end of EVERY set, including the one
        // that leaves nothing pending (`done`). The old guard skipped it —
        // inbox 1074fc37 — but "nothing pending" is not "I have finished": on a
        // one-exercise session the very first set is already `done`, and a set
        // can always be added. Save is the gesture that ends a workout, and
        // Save already resets the timer (useSaveWorkout, both legs). So the
        // only 0 left in the chain is the explicit per-exercise "no rest".
        // ⚠️ restArmed is what the caller uses to decide whether the focus
        // transition waits for a rest or fires immediately: it must stay TRUE
        // exactly when a countdown really started, or a set logged with no rest
        // would never advance.
        restArmed = seconds > 0;
        if (restArmed) {
          restTimer.start(seconds, entry.exerciseId);
          // No invite on a timed HIIT bout (no RIR), nor on the hop to the
          // next superset member: that rest is the implement change, and the
          // real one comes at the end of the round.
          const supersetHop =
            transition.kind === 'exercise' && transition.viaGroupMember;
          restInviteSec = isPrescribedBoutSet(setRow) || supersetHop
            ? 0
            : restInviteSeconds({
                reps: parseLocaleNumber(setRow.reps),
                rir: opts?.rir ?? canonicalRirOf(setRow.rir, setRow.rpe),
                fail: opts?.fail === true,
                resolvedSec: seconds,
              });
        }
      }
      // Bodyweight set just completed during LIVE logging → let the screen
      // stamp Profile.bodyweightNeededAt if the weight is unset (silent: the
      // server derives the bell row). Skipped on past-session edits: the
      // weight may not even apply to that date.
      if (entry.isBodyweight && mode === 'log') {
        onBodyweightSetCompleted?.();
      }
      return {
        prFlash,
        restArmed,
        ...(restInviteSec > 0 ? { restInviteSec } : {}),
      };
    },
    [
      entries,
      dispatch,
      prHistory,
      currentMaxes,
      catalog,
      restTimer,
      defaultRestSec,
      startedAt,
      mode,
      onBodyweightSetCompleted,
    ],
  );
}
